import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchAuthSession } from '../auth/gateway';
import { API_CONFIG } from '../config/api';
import { secureLogger } from '../utils/secureLogger';

interface UseWebSocketOptions {
  onMessage?: (message: any) => void;
  onConnectionChange?: (status: 'connected' | 'disconnected' | 'connecting' | 'error') => void;
  onClose?: (event: CloseEvent) => void;
  reconnectDelay?: number;
}

export const useWebSocket = (user: any, options: UseWebSocketOptions = {}) => {
  const { onMessage, onConnectionChange, onClose, reconnectDelay = 5000 } = options;
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'connecting' | 'error'>('connecting');
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const isConnectingRef = useRef(false);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const manualCloseRef = useRef(false);

  const updateConnectionStatus = useCallback((status: typeof connectionStatus) => {
    setConnectionStatus(status);
    onConnectionChange?.(status);
  }, [onConnectionChange]);

  const connectWebSocket = useCallback(async () => {
    if (!user || isConnectingRef.current || (socketRef.current?.readyState === WebSocket.OPEN)) {
      return;
    }
    
    isConnectingRef.current = true;
    updateConnectionStatus('connecting');
    
    if (socketRef.current) {
      manualCloseRef.current = true;
      socketRef.current.close(1000, 'Reconnecting');
      socketRef.current = null;
    }
    
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      
      if (!token) {
        secureLogger.error('No valid token for WebSocket connection');
        updateConnectionStatus('error');
        return;
      }
      
      const ws = new WebSocket(`${API_CONFIG.WS_URL}/ws/v1/users/${user.user.id}`);
      socketRef.current = ws;
      
      ws.onopen = () => {
        console.log('WS ✅ Connected');
        secureLogger.info('WebSocket connected');
        ws.send(JSON.stringify({
          type: 'auth',
          token,
        }));
        updateConnectionStatus('connected');
        setSocket(ws);
        isConnectingRef.current = false;
        
        // Clear any pending reconnect
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
      };
      
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          onMessage?.(message);
        } catch (error) {
          console.error('WS ❌ Parse error:', event.data);
          secureLogger.error('Failed to parse WebSocket message');
        }
      };
      
      ws.onclose = (event) => {
        console.log(`WS 🔴 Closed: ${event.code} ${event.reason || 'No reason'} ${event.wasClean ? 'Clean' : 'Unclean'}`);
        
        secureLogger.info('WebSocket disconnected', { 
          code: event.code, 
          reason: event.reason,
          wasClean: event.wasClean 
        });
        updateConnectionStatus('disconnected');
        setSocket(null);
        socketRef.current = null;
        isConnectingRef.current = false;
        
        // Call close callback for debugging
        onClose?.(event);
        
        // Skip reconnection if manually closed
        if (manualCloseRef.current) {
          manualCloseRef.current = false;
          return;
        }
        
        // Auto-reconnect logic based on close codes
        const shouldReconnect = user && (
          event.code !== 1000 && // Normal closure
          event.code !== 1001 && // Going away
          event.code !== 4001    // Authentication failed
        );
        
        if (shouldReconnect) {
          console.log(`WS 🔄 Reconnecting in ${reconnectDelay}ms`);
          reconnectTimeoutRef.current = setTimeout(connectWebSocket, reconnectDelay);
        }
      };
      
      ws.onerror = (error) => {
        console.error('WS ❌ Error:', error);
        secureLogger.error('WebSocket error');
        updateConnectionStatus('error');
        isConnectingRef.current = false;
      };
    } catch (error) {
      console.error('WS ❌ Connect failed:', error);
      secureLogger.error('Failed to connect WebSocket');
      updateConnectionStatus('error');
    } finally {
      isConnectingRef.current = false;
    }
  }, [user, onMessage, updateConnectionStatus, reconnectDelay]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    
    if (socketRef.current) {
      socketRef.current.close(1000, 'Manual disconnect');
      socketRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (user?.user?.id && !socketRef.current) {
      connectWebSocket();
    }
    
    return () => {
      disconnect();
    };
  }, [user?.user?.id]);

  return {
    socket,
    connectionStatus,
    connect: connectWebSocket,
    disconnect,
  };
};
